import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PhoenixLogo } from '../components/PhoenixLogo';
import { resetPassword } from '../utils/api';
import { C } from '../utils/constants';

const MIN_PASSWORD_LENGTH = 8; // matches backend rule

// Shared layout shell so the missing-token branch and the form share styling.
// Defined at MODULE scope (not inside ResetPassword) so its component identity
// is stable across renders. Previously it lived inside the render body, so each
// keystroke gave it a new identity and React remounted the whole form — losing
// input focus after one character and snapping scroll to the top.
const Shell = ({ children }) => (
  <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
    <div style={{
      background: `linear-gradient(180deg, #1a0800 0%, #3d1f02 60%, ${C.cream} 100%)`,
      padding: '60px 24px 80px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: 16,
    }}>
      <PhoenixLogo size={220} />

      <h1 style={{
        fontFamily: "'Forum', serif",
        color: '#fff',
        fontSize: 'clamp(1.4rem, 5vw, 2.2rem)',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        marginTop: 8,
      }}>
        Recondition Yourself
      </h1>

      <p style={{
        fontFamily: "'Forum', serif",
        color: C.gold,
        fontSize: '1.1rem',
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        fontWeight: 700,
      }}>
        Client Portal
      </p>
    </div>

    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '0 16px 48px',
      marginTop: -40,
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: '36px 32px',
        width: '100%',
        maxWidth: 420,
        boxShadow: '0 8px 40px rgba(61,31,2,0.15)',
      }}>
        {children}
      </div>
    </div>
  </div>
);

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // No token in URL — link is malformed or someone landed here directly.
  if (!token) {
    return (
      <Shell>
        <h2 style={{
          fontFamily: "'Forum', serif",
          color: C.brown,
          fontSize: '1.6rem',
          letterSpacing: '0.03em',
          marginBottom: 6,
        }}>
          Invalid Reset Link
        </h2>
        <p style={{ color: C.terracotta, fontSize: '0.88rem', marginBottom: 24 }}>
          This reset link is invalid or incomplete.
        </p>
        <p style={{ fontSize: '0.9rem', color: C.brown, lineHeight: 1.5, marginBottom: 8 }}>
          Please request a new reset link to continue.
        </p>
        <Link
          to="/forgot-password"
          className="btn-primary"
          style={{
            display: 'inline-block',
            marginTop: 12,
            textAlign: 'center',
            width: '100%',
            textDecoration: 'none',
          }}
        >
          Request New Reset Link
        </Link>
      </Shell>
    );
  }

  // Successful reset — confirmation + path back to login.
  if (success) {
    return (
      <Shell>
        <h2 style={{
          fontFamily: "'Forum', serif",
          color: C.brown,
          fontSize: '1.6rem',
          letterSpacing: '0.03em',
          marginBottom: 6,
        }}>
          Password Reset
        </h2>
        <p style={{ color: C.terracotta, fontSize: '0.88rem', marginBottom: 24 }}>
          Your password has been updated.
        </p>
        <div style={{
          background: 'rgba(107,62,10,0.08)',
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: '14px 16px',
          marginBottom: 20,
          color: C.brown,
          fontSize: '0.9rem',
          lineHeight: 1.5,
        }}>
          You can now sign in with your new password.
        </div>
        <Link
          to="/login"
          className="btn-primary"
          style={{
            display: 'inline-block',
            marginTop: 8,
            textAlign: 'center',
            width: '100%',
            textDecoration: 'none',
          }}
        >
          Go to Sign In
        </Link>
      </Shell>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, newPassword);
      setSuccess(true);
    } catch (err) {
      // Backend returns 400 for expired / already-used / invalid tokens.
      // 400 doesn't trigger the api.js interceptor (which only acts on 401),
      // so the user stays on this page and sees the message.
      const msg = err?.response?.data?.error || 'Could not reset your password. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Normal path — token present, prompt for a new password.
  return (
    <Shell>
      <h2 style={{
        fontFamily: "'Forum', serif",
        color: C.brown,
        fontSize: '1.6rem',
        letterSpacing: '0.03em',
        marginBottom: 6,
      }}>
        Choose a New Password
      </h2>
      <p style={{ color: C.terracotta, fontSize: '0.88rem', marginBottom: 28 }}>
        Enter your new password below.
      </p>

      <form onSubmit={handleSubmit}>
        {error && (
          <div style={{
            background: 'rgba(173,121,91,0.12)',
            border: `1px solid ${C.terracotta}`,
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 20,
            color: C.terracotta,
            fontSize: '0.85rem',
            lineHeight: 1.4,
          }}>
            {error}
            {/^(.*expired|.*invalid|.*already been used)/i.test(error) && (
              <div style={{ marginTop: 8 }}>
                <Link
                  to="/forgot-password"
                  style={{ color: C.terracotta, textDecoration: 'underline', fontSize: '0.82rem' }}
                >
                  Request a new reset link
                </Link>
              </div>
            )}
          </div>
        )}

        <div style={{ marginBottom: 18 }}>
          <label style={{
            display: 'block',
            fontSize: '0.8rem',
            fontWeight: 600,
            color: C.brown,
            marginBottom: 6,
            letterSpacing: '0.04em',
          }}>
            New Password
          </label>
          <input
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            placeholder="••••••••"
            required
            minLength={MIN_PASSWORD_LENGTH}
            autoComplete="new-password"
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={{
            display: 'block',
            fontSize: '0.8rem',
            fontWeight: 600,
            color: C.brown,
            marginBottom: 6,
            letterSpacing: '0.04em',
          }}>
            Confirm New Password
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
            required
            minLength={MIN_PASSWORD_LENGTH}
            autoComplete="new-password"
          />
        </div>

        <p style={{ fontSize: '0.75rem', color: C.terracotta, margin: '4px 0 20px' }}>
          At least {MIN_PASSWORD_LENGTH} characters.
        </p>

        <button
          type="submit"
          className="btn-primary"
          disabled={loading}
          style={{ width: '100%', fontSize: '1rem' }}
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </button>

        <p style={{ fontSize: '0.85rem', color: C.terracotta, textAlign: 'center', marginTop: 18 }}>
          <Link to="/login" style={{ color: C.terracotta, textDecoration: 'underline' }}>
            Back to sign in
          </Link>
        </p>
      </form>
    </Shell>
  );
};
